/**
 * Panel de Integración con Hacienda
 *
 * Muestra el estado de la integración, el certificado y las acciones de registro.
 * El proveedor que hace de intermediario NO se nombra en pantalla: para el
 * usuario el trámite es ante Hacienda. Los identificadores internos (props,
 * campos del tenant, rutas de la API) sí lo conservan.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Upload,
  RefreshCw,
  History,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import { useTenant, useCertificateStatus } from '@/hooks/useTenant'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

interface AlanubePanelProps {
  onRegister?: () => void
  onRenewCertificate?: () => void
  onToggleInvoicing?: () => Promise<void>
  isToggling?: boolean
}

export function AlanubePanel({
  onRegister,
  onRenewCertificate,
  onToggleInvoicing,
  isToggling = false,
}: AlanubePanelProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { tenant, refreshTenant } = useTenant()
  const {
    isExpiringSoon,
    isExpired,
    expiryDate,
    status: certificateStatus,
  } = useCertificateStatus()

  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshTenant()
    } finally {
      setIsRefreshing(false)
    }
  }

  // Calculate days until certificate expiry
  const getDaysUntilExpiry = () => {
    if (!expiryDate) return null
    const expiry = new Date(expiryDate)
    const now = new Date()
    const diffTime = expiry.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const daysUntilExpiry = getDaysUntilExpiry()

  // Get status color and text
  const getStatusInfo = () => {
    if (!tenant?.is_registered_in_alanube) {
      return {
        color: 'bg-gray-400',
        text: t('settings.statusNotRegistered'),
        description: t('settings.setUpCredentialsToStart'),
      }
    }
    if (isExpired) {
      return {
        color: 'bg-red-500',
        text: t('settings.statusCertificateExpired'),
        description: t('settings.renewCertificateToKeepBilling'),
      }
    }
    if (isExpiringSoon) {
      return {
        color: 'bg-yellow-500',
        text: t('settings.statusCertificateExpiringSoon'),
        description: t('settings.certificateExpiresInDays', { days: daysUntilExpiry ?? 0 }),
      }
    }
    if (certificateStatus !== 'authorized') {
      return {
        color: 'bg-yellow-500',
        text: t('settings.statusCertificatePending'),
        description: t('settings.certificateNotAuthorizedYet'),
      }
    }
    return {
      color: 'bg-green-500',
      text: t('settings.statusConnected'),
      description: t('settings.readyToIssueDocuments'),
    }
  }

  const statusInfo = getStatusInfo()

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('settings.haciendaIntegration')}
            </CardTitle>
            <CardDescription>
              {t('settings.haciendaIntegrationSubtitle')}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status indicator */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <div className={cn('w-4 h-4 rounded-full', statusInfo.color)} />
          <div className="flex-1">
            <p className="font-medium">{statusInfo.text}</p>
            <p className="text-sm text-muted-foreground">{statusInfo.description}</p>
          </div>
        </div>

        {/* Environment badge */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('settings.environment')}</span>
          <Badge variant={tenant?.alanube_environment === 'sandbox' ? 'secondary' : 'default'}>
            {tenant?.alanube_environment === 'sandbox'
              ? t('settings.environmentSandbox')
              : t('settings.environmentProduction')}
          </Badge>
        </div>

        {/* Company ID */}
        {tenant?.alanube_company_id && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('settings.companyId')}</span>
            <code className="text-xs bg-muted px-2 py-1 rounded">
              {tenant.alanube_company_id}
            </code>
          </div>
        )}

        {/* Registration status */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('settings.companyRegistration')}</span>
          {tenant?.is_registered_in_alanube ? (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              {t('settings.companyRegistered')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-muted-foreground">
              <XCircle className="h-4 w-4" />
              {t('settings.companyNotRegistered')}
            </span>
          )}
        </div>

        {/* Certificate status */}
        {tenant?.is_registered_in_alanube && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('settings.digitalCertificate')}</span>
              {certificateStatus === 'authorized' ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('settings.certificateAuthorized')}
                </span>
              ) : certificateStatus === 'pending' ? (
                <span className="flex items-center gap-1 text-yellow-600">
                  <AlertTriangle className="h-4 w-4" />
                  {t('settings.certificatePending')}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-4 w-4" />
                  {isExpired
                    ? t('settings.certificateExpired')
                    : t('settings.certificateNotConfigured')}
                </span>
              )}
            </div>

            {/* Certificate expiry progress */}
            {expiryDate && daysUntilExpiry !== null && daysUntilExpiry > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t('settings.validUntil')}</span>
                  <span>{new Date(expiryDate).toLocaleDateString('es-CR')}</span>
                </div>
                <Progress
                  value={Math.min(100, Math.max(0, (daysUntilExpiry / 365) * 100))}
                  className={cn(
                    'h-2',
                    daysUntilExpiry < 30 ? '[&>div]:bg-red-500' :
                    daysUntilExpiry < 90 ? '[&>div]:bg-yellow-500' : ''
                  )}
                />
                <p className="text-xs text-muted-foreground text-center">
                  {daysUntilExpiry} días restantes
                </p>
              </div>
            )}

            {/* Expiry warning */}
            {(isExpiringSoon || isExpired) && (
              <div className={cn(
                'p-3 rounded-lg text-sm',
                isExpired ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' :
                'bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300'
              )}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">
                      {isExpired
                        ? t('settings.statusCertificateExpired')
                        : t('settings.certificateExpiringSoonTitle')}
                    </p>
                    <p className="text-xs mt-1">
                      {isExpired
                        ? t('settings.cannotIssueUntilRenewed')
                        : t('settings.renewBeforeExpiry')
                      }
                    </p>
                    {onRenewCertificate && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={onRenewCertificate}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        {t('settings.renewCertificate')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Toggle invoicing */}
        {tenant?.is_registered_in_alanube && certificateStatus === 'authorized' && (
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {tenant?.alanube_enabled
                    ? t('settings.invoicingActive')
                    : t('settings.invoicingInactive')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {tenant?.alanube_enabled
                    ? t('settings.invoicingSentAutomatically')
                    : t('settings.invoicingNotSent')
                  }
                </p>
              </div>
              <Switch
                checked={tenant?.alanube_enabled ?? false}
                onCheckedChange={onToggleInvoicing}
                disabled={isToggling || !tenant?.is_active}
              />
            </div>
          </div>
        )}

        {/* Register button */}
        {!tenant?.is_registered_in_alanube && onRegister && (
          <div className="pt-4 border-t">
            <Button className="w-full" onClick={onRegister}>
              <Shield className="h-4 w-4 mr-2" />
              {t('settings.registerCompany')}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              {t('settings.registerCompanyHint')}
            </p>
          </div>
        )}

        {/* View History + Incidents Buttons */}
        {tenant?.is_registered_in_alanube && (
          <div className="pt-4 border-t space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/settings/invoicing-history')}
            >
              <History className="h-4 w-4 mr-2" />
              {t('history.viewHistory')}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/settings/invoicing-incidents')}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {t('incidents.titleShort')}
            </Button>
          </div>
        )}

      </CardContent>
    </Card>
  )
}
